import { PreviewCart } from "../cart/preview-cart";
export class Header extends HTMLElement {
    private static scrollLockCount = 0;
    private static savedScrollY = 0;
    private abortController = new AbortController();
    private panelCache = new Map<string, HTMLElement>();

    constructor() {
        super();

        this.bindEvents();
    }

    disconnectedCallback() {
        this.abortController.abort();
    }

    // -- SG 20260401 iOS-safe scroll lock: position:fixed + save/restore scrollY
    lockScroll() {
        Header.scrollLockCount++;
        if (Header.scrollLockCount === 1) {
            Header.savedScrollY = window.scrollY;
            document.body.style.position = "fixed";
            document.body.style.top = `-${Header.savedScrollY}px`;
            document.body.style.left = "0";
            document.body.style.right = "0";
            document.body.style.overflow = "hidden";
        }
    }

    unlockScroll() {
        if (Header.scrollLockCount === 0) return; // -- SG 20260408 Nothing to unlock; prevents stale scrollY restore
        Header.scrollLockCount--;
        if (Header.scrollLockCount === 0) {
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.left = "";
            document.body.style.right = "";
            document.body.style.overflow = "";
            // -- SG 20260408 Force instant scroll to bypass document scroll-behavior:smooth
            window.scrollTo({ top: Header.savedScrollY, behavior: "instant" });
        }
    }

    bindEvents() {
        const mobileBurgerEle = this.querySelector('.icon-menu-mobile .burger');
        mobileBurgerEle?.addEventListener("click", this.openMobileMegaMenu.bind(this))

        const mobileCloseEle = this.querySelector('.icon-menu-mobile .close');
        mobileCloseEle?.addEventListener("click", this.closeMobileMegaMenu.bind(this));

        const mobileDrawerCloseEle = this.querySelector('.mega-menu__mobile-close');
        mobileDrawerCloseEle?.addEventListener("click", this.closeMobileMegaMenu.bind(this))

        const mobileMenuOverlay = this.querySelector('[data-mobile-menu-overlay]');
        mobileMenuOverlay?.addEventListener("click", this.closeMobileMegaMenu.bind(this));

        const mobileWrapper = this.querySelector('.header-content__mobile-wrapper');
        mobileWrapper?.addEventListener("click", this.handleMobileMenuClick.bind(this));
        mobileWrapper?.addEventListener("click", this.handleLevel2PanelButtonClick.bind(this));
        mobileWrapper?.addEventListener("click", this.handleLevel2AccordionClick.bind(this));
        mobileWrapper?.addEventListener("keydown", this.handleMobileKeydown.bind(this));

        const desktopLevel1List = this.querySelector('.desktop-menu-level1__list');
        if (desktopLevel1List) {
            desktopLevel1List.querySelectorAll('.menu-level1').forEach((el) => {
                el.addEventListener("mouseenter", this.desktopLevel1MouseEnter.bind(this));
                el.addEventListener("mouseleave", this.desktopLevel1MouseLeave.bind(this));
                el.addEventListener("focusin", this.desktopLevel1MouseEnter.bind(this));
            });
            desktopLevel1List.addEventListener("keydown", this.handleDesktopLevel1Keydown.bind(this));
            // -- SG 20260624 Theme editor only: click-to-pin L1 panel (no-op on storefront)
            desktopLevel1List.addEventListener("click", this.desktopLevel1Click.bind(this));
        }
        const sectionHeader = this.closest(".section-header");
        const desktopPanel = sectionHeader?.querySelector(".desktop-level2-panel");
        if (desktopPanel) {
            desktopPanel.addEventListener("mouseenter", this.desktopLevel2PanelMouseEnter.bind(this));
            desktopPanel.addEventListener("mouseleave", this.desktopLevel2SectionMouseLeave.bind(this));
            desktopPanel.addEventListener("click", this.handleLevel2AccordionClick.bind(this));
            desktopPanel.addEventListener("mouseover", this.desktopLevel2ItemHover.bind(this));
            desktopPanel.addEventListener("focusin", this.desktopLevel2ItemFocus.bind(this));
            desktopPanel.addEventListener("keydown", this.handleDesktopLevel2Keydown.bind(this));
        }
        const desktopOverlay = sectionHeader?.querySelector("[data-desktop-panel-overlay]");
        if (desktopOverlay) {
            desktopOverlay.addEventListener("click", this.dismissDesktopLevel2Panel.bind(this));
        }
        if (sectionHeader) {
            sectionHeader.addEventListener("mouseleave", this.desktopLevel2SectionMouseLeave.bind(this));
        }

        document.addEventListener("keydown", this.handleEscapeKey.bind(this), { signal: this.abortController.signal });

        const previewCartIcon = this.querySelector('.icon-pram');
        previewCartIcon?.addEventListener("click", this.openPreviewCart.bind(this));

        const searchIcon = this.querySelector(".icon-search-mobile");
        searchIcon?.addEventListener("click", this.toggleMobileSearchBar.bind(this));

        this.showLevel2PanelIfDebug();
    }

    showLevel2PanelIfDebug() {
        if (!(window as any).Shopify?.designMode) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get("debug-level2-panel") !== "1") return;
        const sectionHeader = this.closest(".section-header");
        const panel = sectionHeader?.querySelector(".desktop-level2-panel") as HTMLElement;
        const firstLevel1 = this.querySelector(".desktop-menu-level1__list .menu-level1");
        const sourcePanel = firstLevel1?.querySelector(".menu-level2-panel--desktop") ?? firstLevel1?.querySelector(".menu-level2-panel");
        if (panel && sourcePanel) {
            panel.innerHTML = "";
            panel.appendChild(sourcePanel.cloneNode(true));
            const firstLevel2 = panel.querySelector(".menu-level2-desktop") as HTMLElement | null;
            if (firstLevel2) this.populateLevel3Panel(firstLevel2);
            panel.classList.add("is-visible");
            panel.setAttribute("aria-hidden", "false");
            sectionHeader?.classList.add("debug-level2-panel-visible");
        }
    }

    handleLevel2AccordionClick(event: Event) {
        const target = event.target as HTMLElement;
        const trigger = target.closest?.(".menu-level2__trigger");
        if (!trigger) return;

        event.preventDefault();
        const level2 = trigger.closest(".menu-level2");
        const content = level2?.querySelector(".menu-level2__content");
        if (!content) return;

        const isOpen = content.classList.toggle("is-open");
        trigger.classList.toggle("accordion-open", isOpen);
        trigger.setAttribute("aria-expanded", String(isOpen));
        if (isOpen && content.closest(".header-content__mobile-wrapper")) {
            requestAnimationFrame(() => this.constrainMobileCalloutHeights(content));
        }
    }

    handleLevel2PanelButtonClick(event: Event) {
        const target = event.target as HTMLElement;

        const backBtn = target.closest?.(".menu-level2-panel__back");
        if (backBtn) {
            event.preventDefault();
            const panel = backBtn.closest(".menu-level2-panel");
            const level1 = panel?.closest(".menu-level1");
            if (level1) level1.classList.remove("is-open");
            return;
        }

        const closeBtn = target.closest?.(".menu-level2-panel__close");
        if (closeBtn) {
            event.preventDefault();
            const panel = closeBtn.closest(".menu-level2-panel");
            const level1 = panel?.closest(".menu-level1");
            if (level1) level1.classList.remove("is-open");
            this.closeMobileMegaMenu();
        }
    }

    toggleMobileSearchBar(_event: Event) {
        this.classList.toggle("mobile-search-open");

        if(this.classList.contains("mobile-mega-menu-open")) {
            const mobileMegaIcon = this.querySelector(".icon-menu-mobile .close") as HTMLElement; 
            mobileMegaIcon?.click();
        }
    }

    handleMobileMenuClick(event: Event) {
        const trigger = (event.target as HTMLElement).closest?.(".menu-level1__trigger");
        if (!trigger) return;
        const level1 = trigger.closest(".menu-level1");
        if (level1) level1.classList.toggle("is-open");
    }

    private desktopLevel2PanelHideTimeout: ReturnType<typeof setTimeout> | null = null;
    private desktopLevel1HoverTimeout: ReturnType<typeof setTimeout> | null = null;
    // -- SG 20260624 Theme editor: keep megamenu open after L1 click (storefront never sets this)
    private designModeDesktopPanelPinned = false;
    private static readonly DESIGN_MODE_PANEL_CLOSE_MS = 250;
    // Matches the panel's close transition in header-megamenu.css -- SG 20261027
    private static readonly PANEL_CLOSE_MS = 250;
    private panelClearTimeout: ReturnType<typeof setTimeout> | null = null;
    // Matches desktop-panel-content-out in header-megamenu.css -- SG 20261027
    private static readonly PANEL_SWITCH_MS = 160;
    // A click this soon after hover or focus opened the menu confirms it rather than closing it -- SG 20261027
    private static readonly CLICK_AFTER_OPEN_MS = 400;
    private panelShownAt = 0;

    private isThemeEditorDesignMode(): boolean {
        return !!(window as any).Shopify?.designMode;
    }

    desktopLevel1MouseEnter(event: Event) {
        if (this.desktopLevel2PanelHideTimeout) {
            clearTimeout(this.desktopLevel2PanelHideTimeout);
            this.desktopLevel2PanelHideTimeout = null;
        }
        if (this.desktopLevel1HoverTimeout) {
            clearTimeout(this.desktopLevel1HoverTimeout);
        }
        const level1 = (event.currentTarget as HTMLElement).closest(".menu-level1");
        this.querySelectorAll(".desktop-menu-level1__list .menu-level1").forEach((el) => el.classList.remove("is-open"));
        level1?.classList.add("is-open");

        this.desktopLevel1HoverTimeout = setTimeout(() => {
            this.showDesktopLevel2Panel(level1);
        }, 50);
    }

    private showDesktopLevel2Panel(level1: Element | null) {
        const sectionHeader = this.closest(".section-header");
        const panel = sectionHeader?.querySelector(".desktop-level2-panel") as HTMLElement;
        const overlay = sectionHeader?.querySelector("[data-desktop-panel-overlay]") as HTMLElement;
        const sourcePanel = level1?.querySelector(".menu-level2-panel--desktop") ?? level1?.querySelector(".menu-level2-panel");
        if (!sourcePanel || !panel) {
            if (!sourcePanel) this.dismissDesktopLevel2Panel();
            return;
        }
        // -- SG 20260401 Cache cloned panels by level1 ID to avoid clone-on-hover (P2)
        const cacheKey = (level1 as HTMLElement)?.id ?? "";
        let cloned = cacheKey ? this.panelCache.get(cacheKey) : undefined;
        if (!cloned) {
            cloned = sourcePanel.cloneNode(true) as HTMLElement;
            Header.deduplicateIds(cloned);
            if (cacheKey) this.panelCache.set(cacheKey, cloned);
        }
        if (this.panelClearTimeout) {
            clearTimeout(this.panelClearTimeout);
            this.panelClearTimeout = null;
        }
        const switching = panel.classList.contains("is-visible");
        // Hover and focus both ask for the menu already showing; rebuilding it would flash -- SG 20261027
        if (switching && cacheKey && panel.dataset.level1 === cacheKey) return;
        panel.classList.toggle("is-switching", switching);
        // Switching, the old menu stays under the new one while they crossfade -- SG 20261027
        const fromHeight = switching ? panel.offsetHeight : 0;
        const leaving = switching ? Array.from(panel.children) : [];
        if (!switching) panel.innerHTML = "";
        panel.querySelectorAll(".is-leaving").forEach((el) => el.remove());
        leaving.forEach((el) => {
            el.classList.add("is-leaving");
            el.setAttribute("aria-hidden", "true");
            el.setAttribute("inert", "");
        });
        const fresh = cloned.cloneNode(true) as HTMLElement;
        panel.prepend(fresh);
        this.panelShownAt = performance.now();
        panel.dataset.level1 = cacheKey;
        const firstLevel2 = fresh.querySelector(".menu-level2-desktop") as HTMLElement | null;
        if (firstLevel2) {
            this.populateLevel3Panel(firstLevel2, false);
            this.setActiveLevel2InPanel(panel, firstLevel2);
        }
        if (switching) {
            setTimeout(() => leaving.forEach((el) => el.remove()), Header.PANEL_SWITCH_MS);
            this.glidePanelHeight(panel, fromHeight);
        }
        panel.classList.add("is-visible");
        panel.setAttribute("aria-hidden", "false");
        sectionHeader?.classList.add("desktop-mega-panel-open");
        // -- SG 20260401 Update aria-expanded on the active level1 desktop trigger
        this.querySelectorAll(".menu-level1__desktop-trigger[aria-expanded]").forEach((t) => t.setAttribute("aria-expanded", "false"));
        level1?.querySelector(".menu-level1__desktop-trigger[aria-expanded]")?.setAttribute("aria-expanded", "true");
        if (overlay) {
            overlay.classList.remove("hidden", "is-closing");
            overlay.setAttribute("aria-hidden", "false");
        }
    }

    // The outline's bottom edge starts at the old menu's height and glides to the new one -- SG 20261027
    private glidePanelHeight(panel: HTMLElement, fromHeight: number) {
        const shift = fromHeight - panel.offsetHeight;
        if (Math.abs(shift) < 2) return;
        panel.classList.add("is-resizing");
        panel.style.setProperty("--panel-shift", `${shift}px`);
        getComputedStyle(panel, "::before").transform;
        panel.classList.remove("is-resizing");
        panel.style.removeProperty("--panel-shift");
    }

    // Opening or switching the whole panel passes animate false, as the outline glide covers the resize -- SG 20261027
    private populateLevel3Panel(level2El: HTMLElement, animate = true) {
        const titleEl = level2El.getAttribute("data-level2-title");
        const contentSource = level2El.querySelector(".menu-level2-desktop__content");
        const panelRoot = level2El.closest(".menu-level2-panel--desktop");
        if (!panelRoot) return;
        const panelTitle = panelRoot.querySelector(".menu-level3-panel__title") as HTMLElement;
        const columnsContainer = panelRoot.querySelector(".menu-level3-panel__columns");
        const level3Panel = panelRoot.querySelector(".menu-level3-panel") as HTMLElement | null;
        if (panelTitle && titleEl !== null) {
            panelTitle.textContent = titleEl;
            const underlineImg = panelRoot.querySelector(".menu-level3-panel__title-underline") as HTMLImageElement | null;
            if (underlineImg) {
                const wordCount = titleEl.trim().split(/\s+/).length;
                const src = wordCount <= 1
                    ? underlineImg.dataset.underlineShort
                    : underlineImg.dataset.underlineLong;
                if (src) underlineImg.src = src;
            }
        }
        if (!columnsContainer || !contentSource) return;

        this.cancelLevel3PanelHeightTransition(panelRoot);
        const currentHeight = panelRoot.getBoundingClientRect().height;
        level3Panel?.classList.add("is-updating");

        // -- SG 20260408 Rebuild columns from scratch to guarantee 1-2-3 DOM order;
        // -- previous approach queried existing columns and appended missing ones at the
        // -- end, scrambling visual order after hovering L2s with different column sets.
        columnsContainer.innerHTML = "";
        const columnEls: HTMLElement[] = [];
        for (let i = 0; i < 3; i++) {
            const col = document.createElement("div");
            col.className = "menu-level3-panel__column";
            col.setAttribute("data-column", String(i + 1));
            columnsContainer.appendChild(col);
            columnEls.push(col);
        }
        const clone = contentSource.cloneNode(true) as HTMLElement;
        for (const child of Array.from(clone.children)) {
            const item = child.querySelector(".menu-level3-item");
            const col = item?.getAttribute("data-column");
            const colIndex = col === "1" ? 0 : col === "2" ? 1 : col === "3" ? 2 : 0;
            const target = columnEls[colIndex];
            if (target) target.appendChild(child);
        }
        columnEls.forEach((el) => {
            if (el.childElementCount === 0) el.remove();
        });

        this.markCalloutOnlyColumns(columnsContainer);
        this.equalizeLevel3RowHeights(columnEls);
        this.constrainCalloutColumnHeights(columnsContainer);

        level3Panel?.classList.remove("is-updating");
        const newHeight = panelRoot.getBoundingClientRect().height;
        const heightDiff = Math.abs(newHeight - currentHeight);
        if (animate && heightDiff > 2) {
            panelRoot.style.height = `${currentHeight}px`;
            panelRoot.style.overflow = "hidden";
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    panelRoot.style.height = `${newHeight}px`;
                });
            });
            const onTransitionEnd = () => {
                panelRoot.style.height = "";
                panelRoot.style.overflow = "";
                panelRoot.removeEventListener("transitionend", onTransitionEnd);
                (panelRoot as HTMLElement & { _heightTransitionEnd?: () => void })._heightTransitionEnd = undefined;
            };
            (panelRoot as HTMLElement & { _heightTransitionEnd?: () => void })._heightTransitionEnd = onTransitionEnd;
            panelRoot.addEventListener("transitionend", onTransitionEnd);
        }
    }

    /**
     * Mark columns that contain only callout items so CSS can make callouts fill height.
     */
    private markCalloutOnlyColumns(columnsContainer: Element) {
        const columns = columnsContainer.querySelectorAll(".menu-level3-panel__column");
        columns.forEach((col) => {
            const children = Array.from(col.children);
            const onlyCallouts =
                children.length > 0 &&
                children.every((wrapper) => wrapper.querySelector(".menu-level3-item.menu-level3-item--callout"));
            col.classList.toggle("menu-level3-panel__column-callouts-only", !!onlyCallouts);
        });
    }

    // -- SG 20260408 Collapse callout columns before measuring so align-items:stretch
    // -- doesn't inflate product column heights to match the (taller) callout content.
    private constrainCalloutColumnHeights(columnsContainer: Element) {
        const columns = Array.from(columnsContainer.querySelectorAll(".menu-level3-panel__column"));
        const calloutColumns = columns.filter((col) =>
            col.classList.contains("menu-level3-panel__column-callouts-only")
        );
        if (calloutColumns.length === 0) return;
        const productColumns = columns.filter(
            (col) => !col.classList.contains("menu-level3-panel__column-callouts-only")
        );
        if (productColumns.length === 0) return;

        // -- Temporarily collapse callout columns so product columns reflect intrinsic height
        calloutColumns.forEach((col) => {
            (col as HTMLElement).style.maxHeight = "0";
        });

        // -- Reading layout forces reflow with callout columns collapsed
        const maxProductHeight = Math.max(...productColumns.map((col) => (col as HTMLElement).getBoundingClientRect().height));
        const targetHeight = Math.ceil(maxProductHeight);

        calloutColumns.forEach((col) => {
            const el = col as HTMLElement;
            if (targetHeight > 0) {
                el.style.minHeight = `${targetHeight}px`;
                el.style.maxHeight = `${targetHeight}px`;
            } else {
                el.style.minHeight = "";
                el.style.maxHeight = "";
            }
        });
    }

    /**
     * On mobile: cap callout height to the content area height so callouts align and don't overflow.
     * Runs when a level2 accordion opens in the mobile menu.
     */
    private constrainMobileCalloutHeights(level2Content: Element) {
        const calloutItems = level2Content.querySelectorAll(".menu-level3-item.menu-level3-item--callout");
        if (calloutItems.length === 0) return;
        const calloutWrappers = Array.from(calloutItems)
            .map((item) => item.closest(".menu-level3"))
            .filter(Boolean) as HTMLElement[];
        if (calloutWrappers.length === 0) return;

        calloutWrappers.forEach((el) => {
            el.style.position = "absolute";
            el.style.visibility = "hidden";
        });
        const contentHeight = Math.ceil((level2Content as HTMLElement).getBoundingClientRect().height);
        calloutWrappers.forEach((el) => {
            el.style.position = "";
            el.style.visibility = "";
        });

        const targetHeight = Math.max(0, contentHeight);
        level2Content.querySelectorAll(".menu-level3-item--callout .menu-level3__callout").forEach((calloutEl) => {
            const el = calloutEl as HTMLElement;
            el.style.maxHeight = targetHeight > 0 ? `${targetHeight}px` : "";
        });
    }

    // -- SG 20260401 Batched reads then writes to avoid layout thrashing (P3, P4)
    private equalizeLevel3RowHeights(columnEls: (HTMLElement | null)[]) {
        const maxRows = Math.max(0, ...columnEls.map((col) => (col ? col.children.length : 0)));
        const rowData: { els: HTMLElement[]; heights: number[] }[] = [];
        for (let row = 0; row < maxRows; row++) {
            const contentEls: HTMLElement[] = [];
            for (const col of columnEls) {
                if (!col || row >= col.children.length) continue;
                const wrapper = col.children[row];
                const item = wrapper?.querySelector(".menu-level3-item:not(.menu-level3-item--callout)");
                const content = item?.querySelector(".menu-level3__content") as HTMLElement | null;
                if (content) contentEls.push(content);
            }
            if (contentEls.length <= 1) continue;
            rowData.push({ els: contentEls, heights: contentEls.map((el) => el.getBoundingClientRect().height) });
        }
        for (const { els, heights } of rowData) {
            const maxH = Math.max(...heights);
            els.forEach((el) => { el.style.minHeight = `${maxH}px`; });
        }
    }

    private cancelLevel3PanelHeightTransition(panelRoot: Element) {
        const el = panelRoot as HTMLElement & { _heightTransitionEnd?: () => void };
        if (el._heightTransitionEnd) {
            panelRoot.removeEventListener("transitionend", el._heightTransitionEnd);
            el._heightTransitionEnd = undefined;
        }
        el.style.height = "";
        el.style.overflow = "";
    }

    desktopLevel1MouseLeave(_event: Event) {
        // Keep level1 hover state when pointer moves to panel
    }

    // -- SG 20260624 Theme editor only: click L1 to pin megamenu open for L2/L3 template hopping
    desktopLevel1Click(event: Event) {
        if (!this.isThemeEditorDesignMode()) {
            this.toggleDesktopLevel2Panel(event);
            return;
        }
        const target = event.target as HTMLElement;
        const trigger = target.closest?.(".menu-level1__desktop-trigger");
        if (!trigger) return;
        const level1 = trigger.closest(".menu-level1");
        const sourcePanel = level1?.querySelector(".menu-level2-panel--desktop, .menu-level2-panel");
        if (!level1 || !sourcePanel) return;

        if (this.desktopLevel1HoverTimeout) {
            clearTimeout(this.desktopLevel1HoverTimeout);
            this.desktopLevel1HoverTimeout = null;
        }
        if (this.desktopLevel2PanelHideTimeout) {
            clearTimeout(this.desktopLevel2PanelHideTimeout);
            this.desktopLevel2PanelHideTimeout = null;
        }

        this.designModeDesktopPanelPinned = true;
        this.querySelectorAll(".desktop-menu-level1__list .menu-level1").forEach((el) => el.classList.remove("is-open"));
        level1.classList.add("is-open");
        this.showDesktopLevel2Panel(level1);
    }

    // Clicking a top-level item opens its menu, or closes it when it is already open -- SG 20261027
    private toggleDesktopLevel2Panel(event: Event) {
        const trigger = (event.target as HTMLElement).closest?.(".menu-level1__desktop-trigger");
        const level1 = trigger?.closest(".menu-level1");
        if (!trigger || !level1) return;
        if (this.desktopLevel1HoverTimeout) {
            clearTimeout(this.desktopLevel1HoverTimeout);
            this.desktopLevel1HoverTimeout = null;
        }
        const panel = this.closest(".section-header")?.querySelector(".desktop-level2-panel") as HTMLElement | null;
        if (panel?.classList.contains("is-visible") && trigger.getAttribute("aria-expanded") === "true") {
            if (performance.now() - this.panelShownAt > Header.CLICK_AFTER_OPEN_MS) this.dismissDesktopLevel2Panel();
            return;
        }
        this.querySelectorAll(".desktop-menu-level1__list .menu-level1").forEach((el) => el.classList.remove("is-open"));
        level1.classList.add("is-open");
        this.showDesktopLevel2Panel(level1);
    }

    // -- SG 20260408 Panel mouseenter keeps panel visible; scroll already locked by showDesktopLevel2Panel
    desktopLevel2PanelMouseEnter() {
        if (this.desktopLevel2PanelHideTimeout) {
            clearTimeout(this.desktopLevel2PanelHideTimeout);
            this.desktopLevel2PanelHideTimeout = null;
        }
    }

    // -- SG 20260624 Storefront: synchronous close unchanged. Editor: debounce or ignore while pinned.
    desktopLevel2SectionMouseLeave() {
        if (this.isThemeEditorDesignMode() && new URLSearchParams(window.location.search).get("debug-level2-panel") === "1") return;
        if (this.isThemeEditorDesignMode()) {
            if (this.designModeDesktopPanelPinned) return;
            this.scheduleDesignModePanelClose();
            return;
        }
        this.closeDesktopLevel2Panel();
    }

    private scheduleDesignModePanelClose() {
        if (this.desktopLevel2PanelHideTimeout) clearTimeout(this.desktopLevel2PanelHideTimeout);
        this.desktopLevel2PanelHideTimeout = setTimeout(() => {
            this.desktopLevel2PanelHideTimeout = null;
            this.closeDesktopLevel2Panel();
        }, Header.DESIGN_MODE_PANEL_CLOSE_MS);
    }

    dismissDesktopLevel2Panel() {
        if (this.desktopLevel2PanelHideTimeout) {
            clearTimeout(this.desktopLevel2PanelHideTimeout);
            this.desktopLevel2PanelHideTimeout = null;
        }
        this.designModeDesktopPanelPinned = false;
        this.closeDesktopLevel2Panel();
    }

    private closeDesktopLevel2Panel() {
        this.designModeDesktopPanelPinned = false;
        const sectionHeader = this.closest(".section-header");
        const panel = sectionHeader?.querySelector(".desktop-level2-panel") as HTMLElement;
        const overlay = sectionHeader?.querySelector("[data-desktop-panel-overlay]") as HTMLElement;
        this.querySelectorAll(".desktop-menu-level1__list .menu-level1").forEach((el) => el.classList.remove("is-open"));
        this.querySelectorAll(".menu-level1__desktop-trigger[aria-expanded]").forEach((t) => t.setAttribute("aria-expanded", "false"));
        sectionHeader?.classList.remove("desktop-mega-panel-open");
        if (panel) {
            panel.classList.remove("is-visible");
            panel.setAttribute("aria-hidden", "true");
            // The bubble closes over the content first (header-megamenu.css) -- SG 20261027
            if (this.panelClearTimeout) clearTimeout(this.panelClearTimeout);
            this.panelClearTimeout = setTimeout(() => {
                this.panelClearTimeout = null;
                if (panel.classList.contains("is-visible")) return;
                panel.innerHTML = "";
                delete panel.dataset.level1;
                overlay?.classList.add("hidden");
            }, Header.PANEL_CLOSE_MS);
            if (overlay) {
                overlay.classList.add("is-closing");
                overlay.setAttribute("aria-hidden", "true");
            }
        }
    }

    desktopLevel2ItemHover(event: Event) {
        const target = (event.target as HTMLElement).closest?.(".menu-level2-desktop") as HTMLElement | null;
        if (!target || target.classList.contains("is-active")) return; // -- SG 20260408 Skip if already active
        this.populateLevel3Panel(target);
        const panel = target.closest(".desktop-level2-panel") as HTMLElement | null;
        if (panel) this.setActiveLevel2InPanel(panel, target);
    }

    private setActiveLevel2InPanel(panel: HTMLElement, level2El: HTMLElement) {
        panel.querySelectorAll(".menu-level2-desktop").forEach((el) => el.classList.remove("is-active"));
        level2El.classList.add("is-active");
    }

    openMobileMegaMenu() {
        const burgerIconEle = this.querySelector('.icon-menu-mobile .burger');
        const closeIconEle = this.querySelector(".icon-menu-mobile .close");
        const mobileMenuEle = this.querySelector('.header-content__mobile-wrapper');

        if(this.classList.contains("mobile-search-open")) {
            const searchIcon = this.querySelector(".icon-search-mobile") as HTMLElement;
            searchIcon?.click();
        }

        burgerIconEle?.classList.add('hide');
        closeIconEle?.classList.remove('hide');
        mobileMenuEle?.classList.add("open");
        mobileMenuEle?.setAttribute("role", "dialog");
        mobileMenuEle?.setAttribute("aria-modal", "true");
        this.classList.add("mobile-mega-menu-open");
        this.querySelectorAll(".menu-level1.is-open").forEach((el) => el.classList.remove("is-open"));

        const sectionHeader = this.closest(".section-header");
        sectionHeader?.classList.add("highest-z-index");
        this.querySelector("[data-mobile-menu-overlay]")?.classList.remove("hidden");
        this.querySelector("[data-mobile-menu-overlay]")?.setAttribute("aria-hidden", "false");
        this.lockScroll();
        // -- SG 20260401 Focus trap + move focus to close button (A4, A6)
        const closeBtn = mobileMenuEle?.querySelector(".mega-menu__mobile-close") as HTMLElement;
        closeBtn?.focus();
        this.trapFocusIn(mobileMenuEle as HTMLElement);
    }

    closeMobileMegaMenu() {
        const burgerIconEle = this.querySelector('.icon-menu-mobile .burger') as HTMLElement;
        const closeIconEle = this.querySelector(".icon-menu-mobile .close");
        const mobileMenuEle = this.querySelector('.header-content__mobile-wrapper');

        burgerIconEle?.classList.remove('hide');
        closeIconEle?.classList.add('hide');
        mobileMenuEle?.classList.remove("open");
        mobileMenuEle?.removeAttribute("role");
        mobileMenuEle?.removeAttribute("aria-modal");
        this.classList.remove("mobile-mega-menu-open");

        this.querySelector("[data-mobile-menu-overlay]")?.classList.add("hidden");
        this.querySelector("[data-mobile-menu-overlay]")?.setAttribute("aria-hidden", "true");
        this.closest(".section-header")?.classList.remove("highest-z-index");
        this.releaseFocusTrap();
        this.unlockScroll();
        // The burger floats in view, but Safari still scrolls to it and undoes the restore -- SG 20261026
        burgerIconEle?.focus({ preventScroll: true });
    }

    private handleEscapeKey(event: KeyboardEvent) {
        if (event.key !== "Escape") return;
        if (this.classList.contains("mobile-mega-menu-open")) {
            this.closeMobileMegaMenu();
        }
        this.dismissDesktopLevel2Panel();
    }

    private handleMobileKeydown(event: KeyboardEvent) {
        if (event.key !== "Enter" && event.key !== " ") return;
        const target = event.target as HTMLElement;
        const trigger = target.closest?.("[role='button'][tabindex='0']");
        if (trigger) {
            event.preventDefault();
            trigger.dispatchEvent(new MouseEvent("click", { bubbles: true }));
        }
    }

    // -- SG 20260401 Enter/Space on desktop level1 triggers opens panel
    private handleDesktopLevel1Keydown(event: KeyboardEvent) {
        if (event.key !== "Enter" && event.key !== " ") return;
        const target = event.target as HTMLElement;
        const trigger = target.closest?.(".menu-level1__desktop-trigger");
        if (!trigger) return;
        event.preventDefault();
        const level1 = trigger.closest(".menu-level1");
        this.querySelectorAll(".desktop-menu-level1__list .menu-level1").forEach((el) => el.classList.remove("is-open"));
        level1?.classList.add("is-open");
        this.showDesktopLevel2Panel(level1);
    }

    // -- SG 20260401 Enter/Space on desktop level2 triggers populates level3
    private handleDesktopLevel2Keydown(event: KeyboardEvent) {
        if (event.key !== "Enter" && event.key !== " ") return;
        const target = event.target as HTMLElement;
        const level2Trigger = target.closest?.(".menu-level2-desktop__trigger");
        if (!level2Trigger) return;
        event.preventDefault();
        const level2 = level2Trigger.closest(".menu-level2-desktop") as HTMLElement | null;
        const panel = level2Trigger.closest(".desktop-level2-panel") as HTMLElement | null;
        if (level2 && panel) {
            this.populateLevel3Panel(level2);
            this.setActiveLevel2InPanel(panel, level2);
        }
    }

    // -- SG 20260401 Focus on desktop level2 item populates level3 (keyboard tab nav)
    private desktopLevel2ItemFocus(event: Event) {
        const target = (event.target as HTMLElement).closest?.(".menu-level2-desktop__trigger");
        if (!target) return;
        const level2 = target.closest(".menu-level2-desktop") as HTMLElement | null;
        const panel = target.closest(".desktop-level2-panel") as HTMLElement | null;
        if (level2 && panel && !level2.classList.contains("is-active")) { // -- SG 20260408 Skip if already active
            this.populateLevel3Panel(level2);
            this.setActiveLevel2InPanel(panel, level2);
        }
    }

    // -- SG 20260401 Focus trap for modal-like drawers (A4, A5)
    private focusTrapHandler: ((e: KeyboardEvent) => void) | null = null;

    private trapFocusIn(container: HTMLElement | null) {
        if (!container) return;
        this.releaseFocusTrap();
        this.focusTrapHandler = (e: KeyboardEvent) => {
            if (e.key !== "Tab") return;
            const focusable = container.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), [tabindex="0"], input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
            );
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        };
        document.addEventListener("keydown", this.focusTrapHandler);
    }

    private releaseFocusTrap() {
        if (this.focusTrapHandler) {
            document.removeEventListener("keydown", this.focusTrapHandler);
            this.focusTrapHandler = null;
        }
    }

    // -- SG 20260401 Rewrite IDs in cloned DOM to prevent duplicates (A10)
    private static deduplicateIds(root: HTMLElement) {
        const suffix = `-dp${Date.now()}`;
        const idMap = new Map<string, string>();
        root.querySelectorAll("[id]").forEach((el) => {
            const oldId = el.id;
            const newId = oldId + suffix;
            idMap.set(oldId, newId);
            el.id = newId;
        });
        root.querySelectorAll("[aria-controls]").forEach((el) => {
            const ref = el.getAttribute("aria-controls") ?? "";
            if (idMap.has(ref)) el.setAttribute("aria-controls", idMap.get(ref)!);
        });
        root.querySelectorAll("[aria-labelledby]").forEach((el) => {
            const ref = el.getAttribute("aria-labelledby") ?? "";
            if (idMap.has(ref)) el.setAttribute("aria-labelledby", idMap.get(ref)!);
        });
    }

    openPreviewCart(_event: Event) {
        const previewCart = new PreviewCart();
        previewCart.openPreviewCart();
    }
}

customElements.define("header-wrapper", Header);